import React, { useState } from 'react';
import { Calendar, X, Gift, CalendarCheck2, ChevronLeft, ChevronRight as ChevronRightIcon } from 'lucide-react';

// ── Tooltip ──────────────────────────────────────────────────────────────────
const HolidayTooltip = ({ holiday, position, onMouseLeave }) => {
  if (!holiday) return null;
  return (
    <div
      className="fixed z-50 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3 min-w-[180px] pointer-events-none"
      style={{ top: position.y - 8, left: position.x + 12, transform: 'translateY(-100%)' }}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex items-start gap-2">
        <div className="flex items-center justify-center flex-shrink-0 rounded-full w-7 h-7 bg-red-50 dark:bg-red-900/30">
          <Gift className="w-3.5 h-3.5 text-red-500 dark:text-red-400" />
        </div>
        <div>
          <p className="text-xs font-semibold leading-tight text-gray-800 dark:text-white">{holiday.name}</p>
          <p className="mt-0.5 text-[10px] text-gray-400 dark:text-gray-500">{holiday.formattedDate}</p>
          <span className="inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 font-medium">
            Public Holiday
          </span>
        </div>
      </div>
    </div>
  );
};

// ── Main Modal ────────────────────────────────────────────────────────────────
const HolidaysModal = ({ isOpen, onClose, holidaysData, holidaysByMonth, formatDate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  if (!isOpen) return null;

  const currentYear  = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const getDaysInMonth   = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

  const getHolidaysForMonth = () => {
    const key = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    return holidaysByMonth[key] || [];
  };

  const getHolidayDetails = (day) =>
    getHolidaysForMonth().find(h => h.date.getDate() === day) || null;

  const handleDateHover = (e, day) => {
    const holiday = getHolidayDetails(day);
    if (holiday) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({ x: rect.left + rect.width / 2, y: rect.top });
      setHoveredDate(day);
    }
  };

  const handleDateLeave = () => setHoveredDate(null);

  const getUpcomingHolidays = () => {
    const today = new Date();
    const limit = new Date(today);
    limit.setDate(today.getDate() + 30);
    return Object.values(holidaysByMonth)
      .flat()
      .filter(h => h.date >= today && h.date <= limit)
      .sort((a, b) => a.date - b.date);
  };

  const upcomingHolidays   = getUpcomingHolidays();
  const hoveredHolidayData = hoveredDate ? getHolidayDetails(hoveredDate) : null;

  const weekdays    = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay    = getFirstDayOfMonth(currentYear, currentMonth);
  const today       = new Date();

  // Build calendar cells
  const cells = [];
  for (let i = 0; i < firstDay; i++) {
    cells.push(
      <div key={`e-${i}`} className="rounded-md h-9 bg-gray-50 dark:bg-gray-800/30" />
    );
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const holiday = getHolidayDetails(day);
    const isToday =
      today.getDate() === day &&
      today.getMonth() === currentMonth &&
      today.getFullYear() === currentYear;

    cells.push(
      <div
        key={day}
        onMouseEnter={e => handleDateHover(e, day)}
        onMouseLeave={handleDateLeave}
        className={[
          'relative h-9 rounded-md flex flex-col items-center justify-center cursor-default transition-all duration-150 select-none',
          holiday
            ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30'
            : 'bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 hover:border-blue-300 dark:hover:border-blue-700/40',
          isToday ? 'ring-2 ring-blue-500 dark:ring-blue-400 ring-offset-1 dark:ring-offset-gray-900' : '',
        ].join(' ')}
      >
        <span className={[
          'text-xs font-medium leading-none',
          holiday ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300',
          isToday ? 'font-bold' : '',
        ].join(' ')}>
          {day}
        </span>

        {/* Holiday dot */}
        {holiday && (
          <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-red-500 dark:bg-red-400" />
        )}

        {/* Holiday name — only visible on wider cells if name fits */}
        {holiday && (
          <span className="mt-0.5 text-[9px] text-red-500 dark:text-red-400 leading-none truncate w-full text-center px-0.5 hidden sm:block">
            {holiday.name.length > 8 ? holiday.name.slice(0, 7) + '…' : holiday.name}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pb-14 bg-black/50 dark:bg-black/70 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 w-full max-w-md max-h-[90vh] overflow-y-auto">

        {/* ── Header ── */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3.5 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-md bg-blue-100/20 dark:bg-blue-900/20">
              <CalendarCheck2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="text-sm font-semibold tracking-tight text-gray-800 dark:text-white">
              Holidays Calendar
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 transition-colors rounded-md hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">

          {/* ── Upcoming banner ── */}
          {upcomingHolidays.length > 0 && (
            <div className="px-3 py-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold text-red-700 dark:text-red-300 mb-2">
                <Gift className="w-3 h-3" />
                Upcoming — Next 30 Days
              </p>
              <div className="flex flex-wrap gap-1.5">
                {upcomingHolidays.map((h, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 text-[10px] text-red-600 dark:text-red-400 font-medium shadow-sm"
                  >
                    <Calendar className="w-2.5 h-2.5" />
                    {h.name} · {h.formattedDate}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Calendar ── */}
          {holidaysData && holidaysData.length > 0 ? (
            <div>
              {/* Month nav */}
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={() => setCurrentDate(new Date(currentYear, currentMonth - 1, 1))}
                  className="p-1 text-gray-500 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-400"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-semibold text-gray-800 dark:text-white">
                  {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <button
                  onClick={() => setCurrentDate(new Date(currentYear, currentMonth + 1, 1))}
                  className="p-1 text-gray-500 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-400"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>

              {/* Weekday row */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {weekdays.map(d => (
                  <div key={d} className="text-center text-[10px] font-semibold text-gray-400 dark:text-gray-500 py-1 uppercase tracking-wide">
                    {d}
                  </div>
                ))}
              </div>

              {/* Day grid */}
              <div className="grid grid-cols-7 gap-1">
                {cells}
              </div>
            </div>
          ) : (
            <div className="py-10 text-center">
              <div className="flex items-center justify-center w-10 h-10 mx-auto mb-3 bg-gray-100 rounded-full dark:bg-gray-800">
                <Calendar className="w-5 h-5 text-gray-400 dark:text-gray-600" />
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500">No holidays found</p>
            </div>
          )}

          {/* ── Legend ── */}
          {holidaysData && holidaysData.length > 0 && (
            <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 border border-red-200 rounded-sm dark:border-red-800 bg-red-50 dark:bg-red-900/20" />
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Holiday</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-white border border-gray-200 rounded-sm dark:border-gray-700 dark:bg-gray-900" />
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Regular Day</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm ring-2 ring-blue-500 dark:ring-blue-400" />
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Today</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-red-500 rounded-full dark:bg-red-400" />
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Holiday Indicator</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating tooltip */}
      {hoveredHolidayData && (
        <HolidayTooltip
          holiday={hoveredHolidayData}
          position={tooltipPosition}
          onMouseLeave={handleDateLeave}
        />
      )}
    </div>
  );
};

export default HolidaysModal;